import React, { useEffect, useState } from "react";
import { Share, Plus, X, Download, Smartphone } from "lucide-react";

// LocalStorage keys
const LOGIN_COUNT_KEY = "gt_login_count";
const DISMISSED_KEY = "gt_pwa_dismissed";
const INSTALLED_KEY = "gt_pwa_installed";

// Detect platform from user-agent. Returns "ios" | "android" | "other".
function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "other";
}

// True if the page is already running as a standalone PWA.
function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator?.standalone) return true; // iOS Safari
  return false;
}

/**
 * One-time install nudge after a member's 3rd login.
 *  - Android (any Chromium): catches `beforeinstallprompt`, shows a single
 *    button that fires the native install dialog.
 *  - iOS Safari: shows hand-rolled instructions ("Tap Share → Add to Home Screen").
 *  - Already-installed users: nothing.
 *  - Dismissed users: nothing — and stays dismissed forever.
 */
export default function PwaInstallPrompt() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [deferred, setDeferred] = useState(null);

  // Capture Android's native install event the moment the browser fires it.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, "1");
      setShow(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Decide whether to show the banner based on login count + dismiss flag.
  useEffect(() => {
    if (localStorage.getItem(INSTALLED_KEY)) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;
    if (isStandalone()) return;

    const count = Number(localStorage.getItem(LOGIN_COUNT_KEY) || "0") + 1;
    localStorage.setItem(LOGIN_COUNT_KEY, String(count));
    if (count < 3) return;

    const p = detectPlatform();
    if (p === "other") return; // Desktop / unknown — don't bother
    setPlatform(p);
    setShow(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setShow(false);
  };

  const installAndroid = async () => {
    if (!deferred) {
      // Chrome hasn't fired the event yet — instruct manually.
      return;
    }
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      localStorage.setItem(INSTALLED_KEY, "1");
    }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="mb-6 border border-[#E5B43A] bg-gradient-to-r from-[#E5B43A]/15 to-transparent px-5 py-4"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:grid w-12 h-12 bg-[#E5B43A] place-items-center shrink-0">
          <Smartphone size={20} className="text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] font-bold mb-1">
            Add Gain Time to Your Home Screen
          </div>
          <div className="text-sm text-zinc-200 mb-3">
            Launch your workouts &amp; meal plans like a native app — full-screen, one-tap, no Safari/Chrome chrome.
          </div>

          {platform === "ios" && (
            <div className="text-xs text-zinc-400 leading-relaxed space-y-1.5" data-testid="pwa-ios-steps">
              <div className="flex items-center gap-2">
                <span className="text-[#E5B43A] font-bold">1.</span>
                Tap the <Share size={12} className="inline text-[#E5B43A]" /> <b className="text-white">Share</b> icon in Safari's bottom bar
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#E5B43A] font-bold">2.</span>
                Scroll down and tap <Plus size={12} className="inline text-[#E5B43A]" /> <b className="text-white">Add to Home Screen</b>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#E5B43A] font-bold">3.</span>
                Tap <b className="text-white">Add</b> — you're done.
              </div>
            </div>
          )}

          {platform === "android" && (
            <div data-testid="pwa-android-prompt">
              {deferred ? (
                <button
                  onClick={installAndroid}
                  data-testid="pwa-install-button"
                  className="inline-flex items-center gap-2 bg-[#E5B43A] text-black px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors"
                >
                  <Download size={13} /> Install Gain Time App
                </button>
              ) : (
                <div className="text-xs text-zinc-400 leading-relaxed space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[#E5B43A] font-bold">1.</span>
                    Tap the <b className="text-white">⋮ menu</b> in Chrome's top-right
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#E5B43A] font-bold">2.</span>
                    Tap <b className="text-white">Install app</b> (or <b className="text-white">Add to Home screen</b>)
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#E5B43A] font-bold">3.</span>
                    Confirm — Gain Time lives on your home screen.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="pwa-dismiss"
          className="text-zinc-500 hover:text-white shrink-0"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
